/**
 * /pending-approval — ver `MEMBERSHIP_ROUTE.PENDING_APPROVAL`
 *
 * Espera de aprobación del equipo (membresía). Polling a Clerk + sync membresía.
 * Cuando `approved` → redirección a `MEMBERSHIP_ROUTE.MI_ACCESO`.
 *
 * Mobile / PWA UX: safe-area, CTAs al pulgar, pasos visuales, feedback live.
 */

import { MembershipPortalHint } from "@/components/membership/MembershipPortalHint";
import { useToast } from "@/hooks/use-toast";
import { useSEO } from "@/hooks/useSEO";
import { getUserDisplayName } from "@/lib/auth";
import {
  BRAND_PERSON_NAME,
  BRAND_SITE_URL,
  BRAND_SUPPORT_EMAIL,
} from "@/lib/brandConfig";
import { MEMBERSHIP_ROUTE } from "@/lib/membershipAccessFlow";
import {
  getPendingApprovalStrings,
  resolveMembershipFlowLanguage,
} from "@/lib/membershipFlowI18n";
import { cn } from "@/lib/utils";
import { useClerk, useSession, useUser } from "@clerk/clerk-react";
import {
  Bell,
  Check,
  CheckCircle2,
  Clock,
  Loader2,
  LogOut,
  Mail,
  MessageCircle,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

const POLL_INTERVAL_MS = 12_000;
const MEMBERSHIP_SYNC_MIN_MS = 60_000;
const NOTIFY_COOLDOWN_MS = 30 * 60 * 1000;
const STORAGE_KEY_PREFIX = "pending_approval_notify_";

const FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Segoe UI", system-ui, sans-serif';

export default function PendingApproval() {
  const { user, isLoaded: userLoaded } = useUser();
  const { session, isLoaded: sessionLoaded } = useSession();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const lang = useMemo(
    () => resolveMembershipFlowLanguage(searchParams, pathname),
    [searchParams, pathname],
  );
  const i18n = useMemo(() => getPendingApprovalStrings(lang), [lang]);
  useSEO({
    title: i18n.seoTitle,
    description: i18n.seoDescription(BRAND_PERSON_NAME),
    keywords: "",
    ogImage: `${BRAND_SITE_URL}/og.png`,
    canonical: `${BRAND_SITE_URL}${MEMBERSHIP_ROUTE.PENDING_APPROVAL}`,
    robots: "noindex, nofollow" as const,
  });

  const email = user?.primaryEmailAddress?.emailAddress ?? "";
  const displayName = getUserDisplayName(user);
  const fromVip =
    searchParams.get("from_vip") === "1" ||
    searchParams.get("from_special_offer") === "1";
  const planTierHint = (searchParams.get("plan_tier") || "")
    .trim()
    .toLowerCase();
  const fromBasicPurchase =
    searchParams.get("from_basic") === "1" || planTierHint === "basic";
  const fromStandardPurchase =
    searchParams.get("from_standard") === "1" ||
    planTierHint === "standard";
  const isAccessRequest = searchParams.get("access_request") === "1";
  const postCheckoutMessaging =
    !isAccessRequest &&
    (fromVip ||
      fromBasicPurchase ||
      fromStandardPurchase ||
      planTierHint === "devil");

  const [approved, setApproved] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [polling, setPolling] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [manualChecking, setManualChecking] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [hasNotified, setHasNotified] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  const approvalHandledRef = useRef(false);
  const lastSyncAtRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  const clearAll = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
  }, []);

  useEffect(() => {
    if (!email) return;
    const storageKey = `${STORAGE_KEY_PREFIX}${email}`;
    const tick = () => {
      const last = localStorage.getItem(storageKey);
      if (!last) {
        setCooldownRemaining(0);
        setHasNotified(false);
        return;
      }
      const ts = parseInt(last, 10);
      if (isNaN(ts)) {
        localStorage.removeItem(storageKey);
        setCooldownRemaining(0);
        setHasNotified(false);
        return;
      }
      const remaining = NOTIFY_COOLDOWN_MS - (Date.now() - ts);
      if (remaining <= 0) {
        localStorage.removeItem(storageKey);
        setCooldownRemaining(0);
        setHasNotified(false);
      } else {
        setCooldownRemaining(remaining);
        setHasNotified(true);
      }
    };
    tick();
    cooldownRef.current = setInterval(tick, 1000);
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, [email]);

  const handleApproved = useCallback(() => {
    if (approvalHandledRef.current) return;
    approvalHandledRef.current = true;
    clearAll();
    if (!mountedRef.current) return;
    setApproved(true);
    try {
      if (typeof navigator !== "undefined" && "vibrate" in navigator) {
        navigator.vibrate?.(40);
      }
    } catch {
      /* ignore */
    }
    let secs = 3;
    setCountdown(secs);
    countdownRef.current = setInterval(() => {
      secs -= 1;
      if (secs <= 0) {
        if (countdownRef.current) clearInterval(countdownRef.current);
        navigate(MEMBERSHIP_ROUTE.MI_ACCESO, { replace: true });
      } else if (mountedRef.current) {
        setCountdown(secs);
      }
    }, 1000);
  }, [clearAll, navigate]);

  const checkApproval = useCallback(
    async (isManual = false) => {
      if (!user || !mountedRef.current) return;
      if (isManual && mountedRef.current) setManualChecking(true);
      if (mountedRef.current) setPolling(true);
      try {
        if (session) {
          const now = Date.now();
          const shouldSync =
            isManual ||
            lastSyncAtRef.current === 0 ||
            now - lastSyncAtRef.current >= MEMBERSHIP_SYNC_MIN_MS;
          if (shouldSync) {
            const token = await session.getToken();
            if (token) {
              lastSyncAtRef.current = now;
              try {
                await fetch("/api/sync-my-membership", {
                  headers: { Authorization: `Bearer ${token}` },
                });
              } catch {
                /* ignore */
              }
            }
          }
        }
        const refreshed = await user.reload();
        if (!mountedRef.current) return;
        setLastChecked(new Date());
        // Invite only after admin approval WITH telegram link (not bare VIP assign).
        const meta = refreshed.publicMetadata as Record<string, unknown> | undefined;
        const hasInvite =
          typeof meta?.telegram_invite_url === "string" &&
          meta.telegram_invite_url.trim().startsWith("http");
        if (meta?.approved === true && hasInvite) {
          handleApproved();
          return;
        }
      } catch {
        /* ignore */
      }
      if (mountedRef.current) {
        setPolling(false);
        if (isManual) setManualChecking(false);
      }
    },
    [user, session, handleApproved],
  );

  useEffect(() => {
    if (!user?.id || !sessionLoaded) return;
    const meta = user.publicMetadata as Record<string, unknown> | undefined;
    const hasInvite =
      typeof meta?.telegram_invite_url === "string" &&
      meta.telegram_invite_url.trim().startsWith("http");
    if (meta?.approved === true && hasInvite) {
      handleApproved();
      return;
    }
    void checkApproval();
    intervalRef.current = setInterval(() => {
      void checkApproval();
    }, POLL_INTERVAL_MS);
    return () => clearAll();
  }, [
    user?.id,
    sessionLoaded,
    user?.publicMetadata?.approved,
    user?.publicMetadata?.telegram_invite_url,
    checkApproval,
    handleApproved,
    clearAll,
  ]);

  const notifyAdmin = useCallback(async () => {
    if (!user || !email) return;
    const storageKey = `${STORAGE_KEY_PREFIX}${email}`;
    const last = localStorage.getItem(storageKey);
    if (last) {
      const ts = parseInt(last, 10);
      const remaining = NOTIFY_COOLDOWN_MS - (Date.now() - ts);
      if (remaining > 0) {
        const min = Math.ceil(remaining / 60000);
        toast({
          title: i18n.toastWaitTitle,
          description: i18n.toastWaitDescription(min),
          variant: "destructive",
        });
        return;
      }
    }
    setNotifying(true);
    try {
      const res = await fetch("/api/admin/notify-pending-subscriber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          name: displayName,
          clerkUserId: user.id,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.error === "banned" || data.banned) {
          toast({
            title: i18n.toastBannedTitle,
            description: i18n.toastBannedDescription,
            variant: "destructive",
            duration: 8000,
          });
          setTimeout(() => signOut(), 3000);
          return;
        }
        if (data.error === "cooldown_active") {
          const min = Math.ceil(
            (data.remaining_ms ?? NOTIFY_COOLDOWN_MS) / 60000,
          );
          toast({
            title: i18n.toastWaitTitle,
            description: i18n.toastWaitDescription(min),
            variant: "destructive",
          });
          return;
        }
        throw new Error(data.error || i18n.toastErrorDescription);
      }
      localStorage.setItem(storageKey, Date.now().toString());
      setHasNotified(true);
      setCooldownRemaining(NOTIFY_COOLDOWN_MS);
      try {
        if (typeof navigator !== "undefined" && "vibrate" in navigator) {
          navigator.vibrate?.(20);
        }
      } catch {
        /* ignore */
      }
      toast({
        title: i18n.toastSentTitle,
        description: i18n.toastSentDescription,
      });
    } catch (err) {
      toast({
        title: i18n.toastErrorTitle,
        description:
          err instanceof Error ? err.message : i18n.toastErrorDescription,
        variant: "destructive",
      });
    } finally {
      setNotifying(false);
    }
  }, [user, email, displayName, toast, signOut, i18n]);

  const shellClass =
    "min-h-[100dvh] min-h-screen bg-zinc-950 text-zinc-100 flex flex-col";

  // Loading
  if (!userLoaded || !sessionLoaded) {
    return (
      <div
        className={cn(shellClass, "items-center justify-center")}
        style={{ fontFamily: FONT_FAMILY }}
      >
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-amber-400/80" />
          <p className="text-xs text-zinc-500 tracking-wide">…</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to={MEMBERSHIP_ROUTE.REGISTRO} replace />;

  // Approved transition — full screen celebration
  if (approved) {
    return (
      <div
        className={cn(
          shellClass,
          "items-center justify-center px-5",
          "pt-[max(1.25rem,env(safe-area-inset-top))] pb-[max(1.25rem,env(safe-area-inset-bottom))]",
        )}
        style={{ fontFamily: FONT_FAMILY }}
      >
        <div className="pointer-events-none fixed inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_20%,rgba(16,185,129,0.22),transparent_55%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_80%,rgba(251,191,36,0.12),transparent_50%)]" />
        </div>
        <div className="w-full max-w-sm text-center space-y-5 animate-in fade-in zoom-in-95 duration-300">
          <div className="mx-auto relative">
            <div className="absolute inset-0 blur-2xl bg-emerald-400/30 rounded-full scale-150" />
            <div className="relative mx-auto flex h-20 w-20 items-center justify-center rounded-[1.75rem] bg-emerald-500/20 ring-1 ring-emerald-400/40 shadow-lg shadow-emerald-500/20">
              <CheckCircle2 className="h-10 w-10 text-emerald-300" strokeWidth={2} />
            </div>
          </div>
          <div className="space-y-2">
            <h1 className="text-[1.65rem] font-bold tracking-tight text-emerald-50 flex items-center justify-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-300" />
              {i18n.approvedTitle}
            </h1>
            <p className="text-[15px] text-emerald-100/75 leading-snug px-2">
              {i18n.approvedSubtitle(BRAND_PERSON_NAME)}
            </p>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 px-4 py-2 text-sm text-emerald-100/80 ring-1 ring-emerald-400/25">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {i18n.approvedRedirect(countdown)}
          </div>
        </div>
      </div>
    );
  }

  const notifyDisabled = notifying || (hasNotified && cooldownRemaining > 0);
  const cooldownMin = Math.ceil(cooldownRemaining / 60000);

  const steps = i18n.whatsNextItems(isAccessRequest);

  return (
    <div className={shellClass} style={{ fontFamily: FONT_FAMILY }}>
      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-zinc-950" />
        <div className="absolute -top-24 left-1/2 h-72 w-[28rem] -translate-x-1/2 rounded-full bg-amber-500/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-56 w-56 rounded-full bg-violet-600/10 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(9,9,11,0.85)_70%)]" />
      </div>

      {/* Top safe bar + status */}
      <header
        className={cn(
          "sticky top-0 z-20 border-b border-white/[0.06] bg-zinc-950/75 backdrop-blur-xl",
          "pt-[max(0.5rem,env(safe-area-inset-top))]",
        )}
      >
        <div className="mx-auto flex max-w-md items-center justify-between gap-3 px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-[11px] font-medium uppercase tracking-[0.14em] text-amber-200/70">
              {BRAND_PERSON_NAME}
            </p>
            <p className="truncate text-sm font-semibold text-zinc-100">
              {i18n.almostThereTitle}
            </p>
          </div>
          <div
            className={cn(
              "flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1.5",
              "bg-white/[0.06] ring-1 ring-white/10",
            )}
            aria-live="polite"
          >
            {polling ? (
              <Loader2 className="h-3 w-3 animate-spin text-amber-300" />
            ) : (
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400/60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-400" />
              </span>
            )}
            <span className="text-[11px] font-medium tabular-nums text-zinc-300">
              {polling
                ? i18n.verifyingLabel
                : lastChecked
                  ? i18n.lastCheckedLabel(
                      lastChecked.toLocaleTimeString(i18n.timeLocale, {
                        hour: "2-digit",
                        minute: "2-digit",
                      }),
                    )
                  : i18n.liveLabel}
            </span>
          </div>
        </div>
      </header>

      {/* Scrollable content */}
      <main
        className={cn(
          "mx-auto flex w-full max-w-md flex-1 flex-col px-4",
          "pt-5",
          // space for sticky bottom bar + home indicator
          "pb-[calc(9.5rem+env(safe-area-inset-bottom))]",
        )}
      >
        {/* Hero */}
        <section className="mb-5 text-center">
          <div className="relative mx-auto mb-4 inline-flex">
            <div className="absolute inset-0 scale-125 rounded-full bg-amber-500/25 blur-2xl" />
            <div
              className={cn(
                "relative flex h-[4.5rem] w-[4.5rem] items-center justify-center",
                "rounded-[1.35rem] bg-gradient-to-br from-amber-400/25 to-orange-600/20",
                "ring-1 ring-amber-400/35 shadow-xl shadow-amber-900/30",
              )}
            >
              <Clock className="h-8 w-8 text-amber-200" strokeWidth={1.75} />
            </div>
          </div>
          <h1 className="text-[1.5rem] font-bold tracking-tight text-zinc-50 leading-tight px-1 break-words">
            {i18n.greeting(displayName)}
          </h1>
          <p className="mt-2 text-[14px] leading-relaxed text-zinc-400 px-1">
            {postCheckoutMessaging
              ? i18n.descriptionAfterCheckout(BRAND_PERSON_NAME)
              : i18n.descriptionAccessRequest(BRAND_PERSON_NAME)}
          </p>
        </section>

        {/* Process strip: Done → In review (current) → Access */}
        <section
          className={cn(
            "mb-4 rounded-2xl px-3 py-3.5",
            "bg-white/[0.04] ring-1 ring-white/10",
          )}
          aria-label={i18n.whatsNextTitle}
        >
          <ol className="flex items-center justify-between gap-1">
            {(
              [
                { key: "done", label: i18n.processStepDone, state: "done" as const },
                {
                  key: "review",
                  label: i18n.processStepInReview,
                  state: "current" as const,
                },
                {
                  key: "access",
                  label: i18n.processStepAccess,
                  state: "upcoming" as const,
                },
              ] as const
            ).map((step, idx, arr) => (
              <li key={step.key} className="flex min-w-0 flex-1 items-center">
                <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
                  <span
                    className={cn(
                      "flex h-8 w-8 items-center justify-center rounded-full text-[12px] font-bold",
                      step.state === "done" &&
                        "bg-emerald-500/25 text-emerald-300 ring-1 ring-emerald-400/40",
                      step.state === "current" &&
                        "bg-amber-500/30 text-amber-100 ring-2 ring-amber-400/50 shadow-md shadow-amber-900/30",
                      step.state === "upcoming" &&
                        "bg-white/5 text-zinc-500 ring-1 ring-white/10",
                    )}
                    aria-current={step.state === "current" ? "step" : undefined}
                  >
                    {step.state === "done" ? (
                      <Check className="h-4 w-4" strokeWidth={2.5} />
                    ) : step.state === "current" ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      idx + 1
                    )}
                  </span>
                  <span
                    className={cn(
                      "max-w-full truncate px-0.5 text-center text-[10px] font-semibold uppercase tracking-wide",
                      step.state === "done" && "text-emerald-300/80",
                      step.state === "current" && "text-amber-200",
                      step.state === "upcoming" && "text-zinc-500",
                    )}
                  >
                    {step.label}
                  </span>
                </div>
                {idx < arr.length - 1 && (
                  <div
                    className={cn(
                      "mb-5 h-0.5 w-full max-w-[2.5rem] shrink-0 rounded-full sm:max-w-[3.25rem]",
                      idx === 0 ? "bg-emerald-500/40" : "bg-white/10",
                    )}
                    aria-hidden
                  />
                )}
              </li>
            ))}
          </ol>
        </section>

        {/* Primary status: "In review" — feels like mid-process, not a dead page */}
        <section className="mb-4 space-y-2">
          <button
            type="button"
            disabled
            aria-disabled="true"
            aria-current="step"
            className={cn(
              "flex w-full min-h-[52px] items-center justify-center gap-2.5 rounded-2xl",
              "text-[15px] font-semibold tracking-tight",
              "cursor-default select-none",
              "bg-gradient-to-b from-amber-400/90 to-amber-600/90 text-zinc-950",
              "shadow-lg shadow-amber-900/35 ring-1 ring-amber-300/35",
              "opacity-100",
            )}
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-zinc-900/40" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-zinc-900/70" />
            </span>
            {i18n.inReviewButton}
          </button>
          <p className="px-1 text-center text-[12px] leading-relaxed text-zinc-400">
            {i18n.inReviewHint}
          </p>
        </section>

        {/* Status card */}
        <section
          className={cn(
            "mb-4 rounded-2xl p-4",
            "bg-amber-500/[0.08] ring-1 ring-amber-400/25",
          )}
        >
          <div className="flex gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 ring-1 ring-amber-400/30">
              <Mail className="h-4 w-4 text-amber-200" />
            </div>
            <div className="min-w-0 flex-1 space-y-1.5">
              <p className="text-[13px] font-semibold text-amber-50">
                {i18n.almostThereTitle}
              </p>
              <p className="text-[12.5px] leading-relaxed text-amber-100/70">
                {(() => {
                  const body = i18n.almostThereBody(email);
                  const parts = body.split(email);
                  return parts.length > 1 ? (
                    <>
                      {parts[0]}
                      <span className="inline-block max-w-full truncate align-bottom rounded-md bg-amber-950/50 px-1.5 py-0.5 font-mono text-[11px] text-amber-100 ring-1 ring-amber-500/20">
                        {email}
                      </span>
                      {parts.slice(1).join(email)}
                    </>
                  ) : (
                    body
                  );
                })()}
              </p>
            </div>
          </div>
        </section>

        {/* Steps — PWA checklist */}
        <section
          className={cn(
            "mb-4 rounded-2xl overflow-hidden",
            "bg-white/[0.04] ring-1 ring-white/10",
          )}
        >
          <div className="border-b border-white/[0.06] px-4 py-3">
            <p className="text-[13px] font-semibold text-zinc-100">
              {i18n.whatsNextTitle}
            </p>
          </div>
          <ol className="divide-y divide-white/[0.05]">
            {steps.map((item, idx) => {
              const done = idx === 0; // first step done (request/payment already happened)
              const current = idx === 1; // "In review" — user is here
              return (
                <li
                  key={idx}
                  className="flex items-start gap-3 px-4 py-3.5"
                >
                  <span
                    className={cn(
                      "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold",
                      done &&
                        "bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-400/30",
                      current &&
                        "bg-amber-500/20 text-amber-200 ring-1 ring-amber-400/35",
                      !done &&
                        !current &&
                        "bg-white/5 text-zinc-500 ring-1 ring-white/10",
                    )}
                  >
                    {done ? (
                      <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                    ) : current ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      idx + 1
                    )}
                  </span>
                  <div className="min-w-0 flex-1 pt-0.5">
                    {current && (
                      <span
                        className={cn(
                          "mb-1 inline-flex items-center rounded-full px-2 py-0.5",
                          "text-[10px] font-bold uppercase tracking-wider",
                          "bg-amber-500/20 text-amber-200 ring-1 ring-amber-400/30",
                        )}
                      >
                        {i18n.inReviewButton}
                      </span>
                    )}
                    <p
                      className={cn(
                        "text-[13px] leading-snug",
                        done && "text-zinc-400",
                        current && "text-zinc-100 font-medium",
                        !done && !current && "text-zinc-500",
                      )}
                    >
                      {item}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </section>

        {/* Secondary links */}
        <div className="mt-auto space-y-3 pt-2">
          <MembershipPortalHint className="!text-zinc-500" />
          <p className="text-center text-[11px] text-zinc-600 leading-relaxed">
            {i18n.autoRefresh(POLL_INTERVAL_MS / 1000)}
            {" · "}
            <a
              href={`mailto:${BRAND_SUPPORT_EMAIL}`}
              className="text-zinc-400 underline underline-offset-2 hover:text-zinc-200"
            >
              {i18n.contactLink}
            </a>
          </p>
          <p className="flex items-center justify-center gap-1.5 text-[11px] text-zinc-600">
            <MessageCircle className="h-3 w-3 shrink-0" />
            <span>{i18n.manualTelegramAssignment}</span>
          </p>
          <button
            type="button"
            onClick={() => signOut()}
            className={cn(
              "mx-auto flex items-center gap-1.5 rounded-full px-3 py-2",
              "text-[12px] text-zinc-500 hover:text-zinc-300",
              "active:bg-white/5 transition-colors",
              "min-h-[44px]",
            )}
          >
            <LogOut className="h-3.5 w-3.5" />
            {i18n.signOut}
          </button>
        </div>
      </main>

      {/* Sticky bottom action bar — thumb zone */}
      <div
        className={cn(
          "fixed inset-x-0 bottom-0 z-30",
          "border-t border-white/[0.08] bg-zinc-950/90 backdrop-blur-xl",
          "pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3",
        )}
      >
        <div className="mx-auto flex max-w-md flex-col gap-2.5 px-4">
          <button
            type="button"
            onClick={() => void notifyAdmin()}
            disabled={notifyDisabled}
            className={cn(
              "flex w-full min-h-[52px] items-center justify-center gap-2 rounded-2xl",
              "text-[15px] font-semibold tracking-tight",
              "transition active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100",
              "bg-gradient-to-b from-amber-400 to-amber-600 text-zinc-950",
              "shadow-lg shadow-amber-900/40 ring-1 ring-amber-300/30",
            )}
          >
            {notifying ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {i18n.notifyTeamBusy}
              </>
            ) : hasNotified && cooldownRemaining > 0 ? (
              <>
                <Clock className="h-4 w-4" />
                {i18n.notifyTeamCooldown(cooldownMin)}
              </>
            ) : (
              <>
                <Bell className="h-4 w-4" />
                {i18n.notifyTeamIdle}
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => void checkApproval(true)}
            disabled={manualChecking || polling}
            className={cn(
              "flex w-full min-h-[48px] items-center justify-center gap-2 rounded-2xl",
              "text-[14px] font-medium",
              "bg-white/[0.07] text-zinc-100 ring-1 ring-white/12",
              "transition active:scale-[0.98] active:bg-white/10",
              "disabled:opacity-50 disabled:active:scale-100",
            )}
          >
            {manualChecking ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {i18n.verifyNowBusy}
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                {i18n.verifyNowIdle}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
